import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IFormStepProps } from '../PageWrapper/PageWrapper';
import DI from '../../../Utils/DI';
import GeneralInformation from '../../Components/GeneralInformation/GeneralInformation';
import Utils from '../../../Utils/Utils';
import Constants, {
    FALLBACK_GENDER, GENDERS, DRIVING_LICENSE_REQUIRED_LENGTH, INCOME_RANGES, FALLBACK_INCOME_RANGES
} from '../../../Utils/Constants';
import {
    IUserPersonalInformation,
    IUserDataItem,
    IUserAddressInformation,
    IUserDataList,
    IUserAdditionalInformation
} from '../../Components/GeneralInformation/general-information-types';
import useStepContext from '../../Hooks/useStepContext';
import IDataManager, { IReviewUserDetails } from '../../../Business/IDataManager';
import { getOccupations } from '../../../Utils/Constants/Occupations';
import { MANDATORY_FIELDS } from '../../../Utils/KYCConfigs';
import { IDropdownItem } from '../../Components/Dropdown/Dropdown';

export interface IStandardDocumentData extends IUserPersonalInformation {
  passportNr?: IUserDataItem & { validateDocumentFormat: RegExp };
  voterNr?: IUserDataItem & { validateDocumentFormat: RegExp };
  genericDocumentNr?: IUserDataItem & { validateDocumentFormat: RegExp };
}

export interface IStandardAddressInformation extends IUserAddressInformation {
    province: IUserDataList
}

const StandardReviewDetails: FC<IFormStepProps> = ({ nextStepHandler }) => {
    const { t, i18n } = useTranslation();
    const {
        currentFormStep, whitelistedUser, documentType, setDocumentType, kycDetails
    } = useStepContext();
    const dataManager: IDataManager = DI.get('DataManager');
    const {
        dateOfBirth, firstName, lastName, gender
    } = kycDetails || {};
    const occupations = useMemo(() => getOccupations(i18n.language.toLowerCase()), [i18n.language]);
    const {
        USER_DETAILS,
        userGender,
        userProvince,
        listOfTowns,
        userTown,
        userOccupation,
        userIncome,
        // userRiskRating,
        userIssuePlaces
    } = useMemo(
        (): IReviewUserDetails => dataManager.convertToReviewUserDetails(
            !currentFormStep?.PROPS?.visited, whitelistedUser, []), [currentFormStep]
    );

    const documentNumberField: IUserDataItem & { validateDocumentFormat: RegExp } = (() => {
        const base: Pick<IUserDataItem, 'type' | 'input' | 'acceptanceCriteria'> = {
            type: 'TEXT',
            input: USER_DETAILS.getDocumentNumber(),
            acceptanceCriteria: Utils.withoutSpecialChars
        };

        switch (documentType) {
        case 'PASSPORT':
            return {
                ...base,
                isValid: Utils.validateFieldLength({ value: base.input, min: 9, max: 9 })
                && Utils.withoutSpecialChars(base.input, Constants.DOCUMENT_VALIDATION_RULES.PASSPORT),
                placeHolder: t('generalInformation.passportNr'),
                requiredLength: { min: 9, max: 9 },
                validateDocumentFormat: Constants.DOCUMENT_VALIDATION_RULES.PASSPORT
            };
        case 'VOTER_CARD':
            return {
                ...base,
                isValid: Utils.validateFieldLength({ value: base.input, min: 11, max: 11 })
                    && Utils.withoutSpecialChars(base.input, Constants.DOCUMENT_VALIDATION_RULES.VOTER_CARD),
                placeHolder: t('generalInformation.voterNr'),
                requiredLength: { min: 11, max: 11 },
                validateDocumentFormat: Constants.DOCUMENT_VALIDATION_RULES.VOTER_CARD
            };
        case 'DRIVING_LICENSE':
            return {
                ...base,
                input: USER_DETAILS.getDocumentNumber(),
                isValid: Utils.validateFieldLength({
                    value: USER_DETAILS.getDocumentNumber(),
                    min: DRIVING_LICENSE_REQUIRED_LENGTH.min,
                    max: DRIVING_LICENSE_REQUIRED_LENGTH.max
                }) && Utils.withoutSpecialChars(
                    USER_DETAILS.getDocumentNumber(),
                    Constants.DOCUMENT_VALIDATION_RULES.DRIVING_LICENSE
                ),
                placeHolder: t('generalInformation.drivingLicenseNr'),
                requiredLength: { min: 11, max: 14 },
                acceptanceCriteria: Utils.withoutSpecialChars,
                validateDocumentFormat: Constants.DOCUMENT_VALIDATION_RULES.DRIVING_LICENSE
            };
        default:
            return {
                ...base,
                isValid: Utils.validateFieldLength({ value: base.input, min: 2, max: 12 })
                    && Utils.withoutSpecialChars(base.input, Constants.DOCUMENT_VALIDATION_RULES.OTHERS),
                placeHolder: t('generalInformation.othersNr'),
                requiredLength: { min: 2, max: 12 },
                validateDocumentFormat: Constants.DOCUMENT_VALIDATION_RULES.OTHERS
            };
        }
    })();

    const formatDate = (dateStr: string) => {
        if (!dateStr || dateStr.length !== 8) return dateStr;
        const year = dateStr.slice(0, 4);
        const month = dateStr.slice(4, 6);
        const day = dateStr.slice(6, 8);
        return `${day}/${month}/${year}`;
    };
    const formattedDOB = dateOfBirth ? formatDate(dateOfBirth) : '';
    const dobValue = USER_DETAILS.getDateOfBirth();
    let dobValidation = Utils.validateDate(dobValue);

    if (dobValue && (dobValue.length === 8 || dobValue.includes('/'))) {
        dobValidation = { isValid: true, message: '' };
    }

    const initialGender = (() => {
        if (userGender && userGender.key !== FALLBACK_GENDER.key) {
            return userGender;
        }

        const persistedGender = GENDERS.find(
            (g) => g.id === Number(kycDetails?.gender || USER_DETAILS.getGender())
        );

        return persistedGender && persistedGender.key !== FALLBACK_GENDER.key
            ? persistedGender
            : FALLBACK_GENDER;
    })();

    const [userPersonalInformation, setUserPersonalInformation] = useState<IStandardDocumentData>({
        firstName: {
            type: 'TEXT',
            input: firstName || USER_DETAILS.getFirstName(),
            isValid: Utils.validateFieldLength(
                { value: USER_DETAILS.getFirstName(), min: 3, max: 30 }
            ),
            placeHolder: t('generalInformation.firstName'),
            requiredLength: { min: 3, max: 30 },
            acceptanceCriteria: Utils.unicodeChars
        },
        lastName: {
            type: 'TEXT',
            input: lastName || USER_DETAILS.getLastName(),
            isValid: Utils.validateFieldLength(
                { value: USER_DETAILS.getLastName(), min: 3, max: 30 }
            ),
            placeHolder: t('generalInformation.lastName'),
            requiredLength: { min: 3, max: 30 },
            acceptanceCriteria: Utils.unicodeChars
        },
        dateOfBirth: {
            type: 'DATE',
            input: formattedDOB || USER_DETAILS.getDateOfBirth(),
            placeHolder: 'DD/MM/YYYY',
            isValid:
                (!!USER_DETAILS.getDateOfBirth() && USER_DETAILS.getDateOfBirth().length === 8)
                || (formattedDOB && formattedDOB.includes('/'))
                    ? true
                    : dobValidation.isValid,
            errorMessage:
                (!!USER_DETAILS.getDateOfBirth() && USER_DETAILS.getDateOfBirth().length === 8)
                || (formattedDOB && formattedDOB.includes('/'))
                    ? ''
                    : dobValidation.message,
            requiredLength: { min: 10, max: 10 },
            acceptanceCriteria: Utils.dateCharacters
        },
        gender: {
            type: 'LIST',
            list: GENDERS,
            disabled: false,
            selectedItem: initialGender,
            isValid: initialGender.key !== FALLBACK_GENDER.key,
            isLabelVisible: initialGender.key !== FALLBACK_GENDER.key,
            placeholder: 'GENDER'
        },
        citizen: {
            type: 'LIST',
            list: Constants.MARKET_GEO_INFORMATION.NATIONALITIES,
            disabled: true,
            isValid: true,
            selectedItem: Constants.MARKET_GEO_INFORMATION.NATIONALITIES
                .find((citizenship) => citizenship.key
                === `citizen.${Constants.MARKET_GEO_INFORMATION.DEFAULT_NATIONALITY_KEY}`)
        },
        othersNr: documentNumberField,
        placeOfBirth: {
            type: 'TEXT',
            input: USER_DETAILS.getPlaceOfBirth(),
            isValid: Utils.validateFieldLength(
                { value: USER_DETAILS.getPlaceOfBirth(), min: 3, max: 30 }
            ),
            placeHolder: t('generalInformation.placeOfBirth'),
            requiredLength: { min: 3, max: 30 },
            acceptanceCriteria: Utils.unicodeChars
        }
    });

    const [
        userAddressInformation, setUserAddressInformation
    ] = useState<IStandardAddressInformation>({
        street: {
            type: 'TEXT',
            input: USER_DETAILS.getStreet(),
            isValid: Utils.validateFieldLength(
                { value: USER_DETAILS.getStreet(), min: 3, max: 40 }
            ),
            requiredLength: { min: 3, max: 40 },
            placeHolder: t('generalInformation.street'),
            acceptanceCriteria: Utils.addressStreetCharacters
        },
        province: {
            type: 'LIST',
            isValid: !!userProvince || !!userTown,
            disabled: false,
            selectedItem: userProvince
            || Utils.getProvinceById(userTown?.provinceID)
            || Constants.MARKET_GEO_INFORMATION.FALLBACK_PROVINCE,
            isLabelVisible: !!userProvince || !!userTown,
            list: Constants.MARKET_GEO_INFORMATION.PROVINCES
        },
        town: {
            type: 'LIST',
            isValid: (
                !!userTown
                && (userProvince
                    ? Utils.getTownsByProvinceID(userProvince?.id).includes(userTown)
                    : Utils.getProvinceById(userTown.provinceID)?.id === userTown.provinceID)
            ),
            disabled: false,
            selectedItem: userTown || Constants.MARKET_GEO_INFORMATION.FALLBACK_TOWN,
            isLabelVisible: !!userTown,
            list: userProvince
                ? listOfTowns : Utils.getTownsByProvinceIDFiltered(userTown?.provinceID)
        },
        commune: {
            type: 'TEXT',
            input: USER_DETAILS.getCommune(),
            isValid: Utils.validateFieldLength(
                { value: USER_DETAILS.getCommune(), min: 3, max: 30 }
            ),
            requiredLength: { min: 3, max: 30 },
            acceptanceCriteria: Utils.unicodeChars,
            placeHolder: t('generalInformation.commune')
        }

    });
    const [
        userAdditionalInformation, setUserAdditionalInformation
    ] = useState<IUserAdditionalInformation>({
        occupation: {
            type: 'LIST',
            list: occupations,
            disabled: false,
            isValid: !!userOccupation || !MANDATORY_FIELDS.includes('OCCUPATION'),
            selectedItem: occupations.find(
                (o) => o.value === userOccupation?.value
            ) || Constants.MARKET_GEO_INFORMATION.FALLBACK_OCCUPATION,
            placeHolder: t('generalInformation.occupation')
        },
        income: {
            type: 'LIST',
            list: INCOME_RANGES,
            disabled: false,
            isValid: MANDATORY_FIELDS.includes('INCOME') ? !!userIncome : true,
            selectedItem: userIncome || FALLBACK_INCOME_RANGES,
            placeHolder: t('generalInformation.income')
        },
        idCardIssuingBy: {
            type: 'TEXT',
            input: USER_DETAILS.getidCardIssuingBy() ?? '',
            isValid: MANDATORY_FIELDS.includes('ID_CARD_ISSUING_BY') ? !!USER_DETAILS.getidCardIssuingBy() : true,
            placeHolder: t('generalInformation.idCardIssuingBy'),
            requiredLength: { min: 3, max: 50 },
            acceptanceCriteria: Utils.unicodeChars
        },
        idCardIssuingDate: {
            type: 'DATE',
            input: USER_DETAILS.getidCardIssuingDate() || '',
            placeHolder: 'DD/MM/YYYY',
            isValid: (() => {
                if (!MANDATORY_FIELDS.includes('ID_CARD_ISSUING_DATE')) return true;
                const issuingDate = USER_DETAILS.getidCardIssuingDate();
                if (!issuingDate) return false;
                const result = Utils.validateIdCardIssuingDate(
                    USER_DETAILS.getDateOfBirth(),
                    issuingDate
                );

                return typeof result === 'boolean' ? result : result.isValid;
            })(),
            requiredLength: { min: 10, max: 10 },
            acceptanceCriteria: Utils.dateCharacters
        },
        idCardIssuingPlace: {
            type: 'LIST',
            list: Constants.MARKET_GEO_INFORMATION.idCardIssuingPlace,
            isValid: MANDATORY_FIELDS.includes('ID_CARD_ISSUING_PLACE') ? !!userIssuePlaces : true,
            selectedItem: userIssuePlaces || Constants.MARKET_GEO_INFORMATION.FALLBACK_CARD_ISSUED_PLACE,
            placeHolder: t('generalInformation.idCardIssuingPlace'),
            isLabelVisible: !!userIssuePlaces,
            disabled: false
        }
    });

    return (
        <>
            <GeneralInformation
                nextStepHandler={nextStepHandler}
                userPersonalInformation={userPersonalInformation}
                setUserPersonalInformation={setUserPersonalInformation}
                userAddressInformation={userAddressInformation}
                userAdditionalInformation={userAdditionalInformation}
                setUserAdditionalInformation={setUserAdditionalInformation}
                setUserAddressInformation={setUserAddressInformation}
                documentType={documentType ?? undefined}
            />
        </>
    );
};

export default StandardReviewDetails;
